/**
 * Shared form pieces & helpers for the member profile — used by the admin's
 * Team page (create/edit) and by the member's ProfileCompletionGate.
 */
import { useEffect, useRef, useState } from "react";
import { http, formatApiError, API } from "../lib/api";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Camera, User, Phone, Envelope, IdentificationCard, Calendar, House, Briefcase, UsersThree, UploadSimple } from "@phosphor-icons/react";

export const emptyMember = {
  name: "", email: "", password: "", role: "member",
  contact_no: "", alt_contact_no: "", father_name: "",
  date_of_joining: "", aadhar: "", full_address: "", designation: "",
};

export const DESIGNATION_OPTIONS = [
  "Architect",
  "Junior Architect",
  "Senior Architect",
  "Draftsman",
  "MEP Engineer",
  "HVAC Designer",
  "Home Designer",
  "Site Engineer",
  "Structural Engineer",
  "Interior Designer",
  "Civil Engineer",
  "Project Manager",
  "3D Visualizer",
];

export function formatAadhar(raw) {
  const s = (raw || "").replace(/\D/g, "").slice(0, 12);
  const parts = [];
  for (let i = 0; i < s.length; i += 4) parts.push(s.slice(i, i + 4));
  return parts.join(" ");
}

export function stripAadhar(raw) {
  return (raw || "").replace(/\D/g, "");
}

export function memberPhotoUrl(userId, photoId) {
  if (!userId || !photoId) return null;
  return `${API}/users/${userId}/photo?v=${photoId}`;
}

export function MemberAvatar({ user, size = 48, className = "" }) {
  const photo = user?.photo;
  const url = photo ? memberPhotoUrl(user.id, photo.id) : null;
  const initials = (user?.name || "?")
    .split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
  const s = `${size}px`;
  return (
    <div
      className={`inline-flex items-center justify-center overflow-hidden bg-zinc-900 text-white font-bold border-2 border-zinc-900 ${className}`}
      style={{ width: s, height: s, fontSize: size * 0.4 }}
      data-testid={`member-avatar-${user?.id || "anon"}`}
    >
      {url ? (
        <img src={url} alt={user?.name || "Photo"} className="w-full h-full object-cover" />
      ) : (
        <span>{initials || "?"}</span>
      )}
    </div>
  );
}

export function MemberPhotoUpload({ userId, currentPhoto, onUploaded, size = 96 }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const pick = () => inputRef.current?.click();

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    if (file.size > 1024 * 1024) {
      toast.error("Photo must be under 1MB");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await http.post(`/users/${userId}/photo`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo updated");
      onUploaded?.(data.photo);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex flex-col items-center gap-3">
      <MemberAvatar
        user={{ id: userId, photo: currentPhoto, name: "Photo" }}
        size={size}
        className="rounded-none"
      />
      <button
        type="button"
        onClick={pick}
        disabled={uploading}
        data-testid={`upload-photo-${userId}`}
        className="rounded-none h-9 flex items-center gap-1.5 border border-blue-600 bg-blue-600 text-white hover:bg-blue-700 px-3 text-xs font-mono uppercase tracking-widest disabled:opacity-50"
      >
        <Camera size={12} weight="bold" />
        {uploading ? "Uploading…" : (currentPhoto ? "Replace Photo" : "Upload Photo")}
      </button>
      <input ref={inputRef} type="file" hidden accept="image/*" onChange={upload} />
      <div className="text-[10px] text-zinc-500 font-mono">JPG/PNG/WEBP · ≤ 1 MB</div>
    </div>
  );
}

/**
 * Renders all compulsory profile fields. `mode` = "create" (admin) | "edit" (admin) | "self" (member).
 * - In "create": also renders email + password + role dropdown.
 * - In "edit": renders the role dropdown too (admin-only mount point).
 * - In "self": role/email/password are hidden.
 */
export function MemberFields({ form, setForm, mode = "create" }) {
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));
  const setSelect = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Field label="Full Name *" icon={User}>
        <Input data-testid="mf-name" value={form.name} onChange={set("name")} required
               className="rounded-none border-zinc-400 focus-visible:ring-blue-600 focus-visible:ring-offset-0" />
      </Field>
      {mode === "create" && (
        <Field label="Login Email *" icon={Envelope}>
          <Input data-testid="mf-email" type="email" value={form.email} onChange={set("email")} required
                 className="rounded-none border-zinc-400 focus-visible:ring-blue-600 focus-visible:ring-offset-0" />
        </Field>
      )}
      <Field label="Contact No. *" icon={Phone}>
        <Input data-testid="mf-contact" value={form.contact_no} onChange={set("contact_no")} required
               placeholder="+91 …" inputMode="tel"
               className="rounded-none border-zinc-400 focus-visible:ring-blue-600 focus-visible:ring-offset-0" />
      </Field>
      <Field label="Alternate Contact No. *" icon={Phone}>
        <Input data-testid="mf-alt-contact" value={form.alt_contact_no} onChange={set("alt_contact_no")} required
               inputMode="tel"
               className="rounded-none border-zinc-400 focus-visible:ring-blue-600 focus-visible:ring-offset-0" />
      </Field>
      <Field label="Father's Name *" icon={UsersThree}>
        <Input data-testid="mf-father" value={form.father_name} onChange={set("father_name")} required
               className="rounded-none border-zinc-400 focus-visible:ring-blue-600 focus-visible:ring-offset-0" />
      </Field>
      <Field label="Date of Joining *" icon={Calendar}>
        <Input data-testid="mf-doj" type="date" value={form.date_of_joining}
               onChange={set("date_of_joining")}
               onKeyDown={(e) => e.preventDefault()}
               required
               className="rounded-none border-zinc-400 focus-visible:ring-blue-600 focus-visible:ring-offset-0" />
      </Field>
      <Field label="Aadhaar (12 digits) *" icon={IdentificationCard}>
        <Input data-testid="mf-aadhar" value={formatAadhar(form.aadhar)}
               onChange={(e) => setForm((f) => ({ ...f, aadhar: stripAadhar(e.target.value) }))}
               required inputMode="numeric" maxLength={14} placeholder="XXXX XXXX XXXX"
               className="rounded-none border-zinc-400 focus-visible:ring-blue-600 focus-visible:ring-offset-0 font-mono" />
      </Field>
      <Field label="Designation *" icon={Briefcase}>
        <Select value={form.designation || ""}
                onValueChange={(v) => setForm((f) => ({ ...f, designation: v }))}>
          <SelectTrigger data-testid="mf-designation"
                         className="rounded-none border-zinc-400 focus:ring-blue-600">
            <SelectValue placeholder="Select a designation…" />
          </SelectTrigger>
          <SelectContent>
            {DESIGNATION_OPTIONS.map((d) => (
              <SelectItem key={d} value={d} data-testid={`mf-designation-opt-${d.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}>{d}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <div className="sm:col-span-2">
        <Field label="Full Address *" icon={House}>
          <Textarea data-testid="mf-address" value={form.full_address} onChange={set("full_address")} required
                    rows={2}
                    className="rounded-none border-zinc-400 focus-visible:ring-blue-600 focus-visible:ring-offset-0" />
        </Field>
      </div>
      {mode === "create" && (
        <>
          <Field label="Initial Password *" icon={UploadSimple}>
            <Input data-testid="mf-password" type="text" value={form.password} onChange={set("password")} required
                   minLength={6}
                   placeholder="At least 6 characters"
                   className="rounded-none border-zinc-400 focus-visible:ring-blue-600 focus-visible:ring-offset-0 font-mono" />
          </Field>
          <Field label="Role *" icon={User}>
            <Select value={form.role} onValueChange={setSelect("role")}>
              <SelectTrigger data-testid="mf-role"
                             className="rounded-none border-zinc-400 focus:ring-blue-600 focus:ring-offset-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="member" className="rounded-none">Team Member</SelectItem>
                <SelectItem value="manager" className="rounded-none">Project Manager</SelectItem>
                <SelectItem value="admin" className="rounded-none">Admin</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </>
      )}
      {mode === "edit" && (
        <Field label="Role *" icon={User}>
          <Select value={form.role} onValueChange={setSelect("role")}>
            <SelectTrigger data-testid="mf-role-edit"
                           className="rounded-none border-zinc-400 focus:ring-blue-600 focus:ring-offset-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="member" className="rounded-none" data-testid="mf-role-opt-member">Team Member</SelectItem>
              <SelectItem value="manager" className="rounded-none" data-testid="mf-role-opt-manager">Project Manager</SelectItem>
              <SelectItem value="admin" className="rounded-none" data-testid="mf-role-opt-admin">Admin</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      )}
    </div>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <div>
      <label className="label-overline block mb-1.5 text-zinc-700 flex items-center gap-1.5">
        {Icon && <Icon size={11} weight="bold" className="text-zinc-500" />}
        {label}
      </label>
      {children}
    </div>
  );
}

export function validateMemberForm(form, opts = { requirePassword: true }) {
  const req = ["name", "contact_no", "alt_contact_no", "father_name",
               "date_of_joining", "aadhar", "full_address", "designation"];
  for (const k of req) {
    if (!String(form[k] || "").trim()) return `${k.replace(/_/g, " ")} is required`;
  }
  if (stripAadhar(form.aadhar).length !== 12) return "Aadhaar must be exactly 12 digits";
  if (opts.requirePassword) {
    if (!form.email?.trim()) return "Email is required";
    if (!form.password || form.password.length < 6) return "Password must be at least 6 characters";
  }
  return null;
}
